
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowRight } from 'lucide-react';
import { QuizPreferences } from '@/types/quiz';

interface Destination {
  id: string;
  name: string;
  country: string;
  score: number;
  climate_data?: {
    best_visit_season?: string;
  };
  [key: string]: any;
}

interface DestinationCardProps {
  destination: Destination;
  preferences: QuizPreferences;
  renderHighlights: (destination: Destination, preferences: QuizPreferences) => Array<{ text: string; icon: React.ReactNode; color: string }>;
}

const DestinationCard: React.FC<DestinationCardProps> = ({ destination, preferences, renderHighlights }) => {
  const navigate = useNavigate();

  const handleExploreClick = () => {
    // Use the correct route format that matches App.tsx routing
    const destinationSlug = destination.name.toLowerCase().replace(/\s+/g, '-');
    const countrySlug = destination.country.toLowerCase().replace(/\s+/g, '-');
    navigate(`/destinations/${countrySlug}/${destinationSlug}/${destination.id}`);
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div>
            <div className="text-lg font-medium">{destination.name}</div>
            <div className="text-sm text-muted-foreground">{destination.country}</div>
          </div>

          <Badge variant="outline" className="bg-primary/10 text-primary">
            Match Score: {destination.score}
          </Badge>

          <div className="space-y-2">
            {renderHighlights(destination, preferences).map((highlight, index) => (
              <div key={index} className="flex items-center gap-2">
                <Badge variant="secondary" className={highlight.color}>
                  <span className="flex items-center gap-1">
                    {highlight.icon}
                    {highlight.text}
                  </span>
                </Badge>
              </div>
            ))}
          </div>

          {destination.climate_data?.best_visit_season && (
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="w-4 h-4" />
              <span>Best time to visit: {destination.climate_data.best_visit_season}</span>
            </div>
          )}

          <Button 
            className="w-full"
            onClick={handleExploreClick}
          >
            <span>Explore More</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DestinationCard;
